import { useState } from 'react';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from './ui/dialog';
import { useWorkspace } from '../context/workspace-context';
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
  type Department,
} from '../hooks/use-department';
import { useToast } from './ui/toast';
import { getErrorMessage } from '../helpers/get-error-message';

export default function DepartmentSettings() {
  const { currentWorkspace, isLoadingWorkspaces } = useWorkspace();

  if (isLoadingWorkspaces) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }

  if (!currentWorkspace) {
    return <p className="text-sm text-muted-foreground">No workspace found</p>;
  }

  return <DepartmentSettingsContent organizationId={currentWorkspace.id} />;
}

function DepartmentSettingsContent({
  organizationId,
}: {
  organizationId: number;
}) {
  const { data, isLoading } = useDepartments(organizationId);
  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const deleteDepartment = useDeleteDepartment();
  const { showToast } = useToast();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDepartment, setSelectedDepartment] =
    useState<Department | null>(null);
  const [newDepartmentName, setNewDepartmentName] = useState('');
  const [editDepartmentName, setEditDepartmentName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const departments = data?.departments || [];

  const handleAddDepartment = async () => {
    if (!newDepartmentName.trim()) {
      setError('Department name is required');
      return;
    }

    try {
      setError(null);
      await createDepartment.mutateAsync({
        name: newDepartmentName.trim(),
        organisationId: organizationId,
      });
      setNewDepartmentName('');
      setShowAddDialog(false);
      showToast('Department created', 'success');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create department'));
    }
  };

  const handleEditDepartment = async () => {
    if (!selectedDepartment) return;
    if (!editDepartmentName.trim()) {
      setError('Department name is required');
      return;
    }

    try {
      setError(null);
      await updateDepartment.mutateAsync({
        departmentId: selectedDepartment.id,
        name: editDepartmentName.trim(),
        organisationId: organizationId,
      });
      setSelectedDepartment(null);
      setEditDepartmentName('');
      setShowEditDialog(false);
      showToast('Department updated', 'success');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update department'));
    }
  };

  const handleDeleteDepartment = async () => {
    if (!selectedDepartment) return;

    try {
      setError(null);
      await deleteDepartment.mutateAsync({
        departmentId: selectedDepartment.id,
        organisationId: organizationId,
      });
      setSelectedDepartment(null);
      setShowDeleteDialog(false);
      showToast('Department deleted', 'success');
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete department'));
    }
  };

  const openEditDialog = (department: Department) => {
    setSelectedDepartment(department);
    setEditDepartmentName(department.name);
    setError(null);
    setShowEditDialog(true);
  };

  const openDeleteDialog = (department: Department) => {
    setSelectedDepartment(department);
    setError(null);
    setShowDeleteDialog(true);
  };

  if (isLoading) {
    return (
      <Card className="border shadow-none rounded-lg">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border shadow-none rounded-lg">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium">Departments</h3>
            <Button
              onClick={() => {
                setNewDepartmentName('');
                setError(null);
                setShowAddDialog(true);
              }}
              className="h-8 px-3 bg-accent-purple hover:bg-accent-purple/90 text-white text-xs"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Department
            </Button>
          </div>

          {departments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No departments yet. Add one to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departments.map((department) => (
                  <TableRow key={department.id}>
                    <TableCell className="text-sm">{department.name}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => openEditDialog(department)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => openDeleteDialog(department)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Department Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-base">Add Department</DialogTitle>
            <DialogDescription className="text-xs">
              Create a new department for your organization.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="department-name" className="text-xs">
                Name
              </Label>
              <Input
                id="department-name"
                value={newDepartmentName}
                onChange={(e) => setNewDepartmentName(e.target.value)}
                placeholder="Enter department name"
                className="h-9 text-sm"
              />
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="h-8 px-3 text-xs">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleAddDepartment}
              disabled={createDepartment.isPending}
              className="h-8 px-3 bg-accent-purple hover:bg-accent-purple/90 text-white text-xs"
            >
              {createDepartment.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Department Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-base">Edit Department</DialogTitle>
            <DialogDescription className="text-xs">
              Rename this department.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="edit-department-name" className="text-xs">
                Name
              </Label>
              <Input
                id="edit-department-name"
                value={editDepartmentName}
                onChange={(e) => setEditDepartmentName(e.target.value)}
                placeholder="Enter department name"
                className="h-9 text-sm"
              />
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="h-8 px-3 text-xs">
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleEditDepartment}
              disabled={updateDepartment.isPending}
              className="h-8 px-3 bg-accent-purple hover:bg-accent-purple/90 text-white text-xs"
            >
              {updateDepartment.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Department Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-base">Delete Department</DialogTitle>
            <DialogDescription className="text-xs">
              Are you sure you want to delete "{selectedDepartment?.name}"?
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <p className="text-sm text-muted-foreground">
              This will remove the department from all users and courses. Users
              in this department may lose access to courses assigned to it.
            </p>
            {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="h-8 px-3 text-xs">
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDeleteDepartment}
              disabled={deleteDepartment.isPending}
              className="h-8 px-3 text-xs"
            >
              {deleteDepartment.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
